import React, { useContext, useState, useEffect, useRef } from 'react';
import { OnlineUsersContext } from './OnlineUsersContext';
import { UserContext } from './UserContext';
import Modal from 'react-bootstrap/Modal';
import EmuleProfil from './components/EmuleProfil';
import VideoCallManager from './components/VideoCallManager';
import styles from './styles/components/onlinelateralusers.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimesCircle, faPhone, faVideo } from '@fortawesome/free-solid-svg-icons';
import presenceService from './services/presenceService';
import { useLanguage } from './contexts/LanguageContext';
import UserXpAvatar from './components/UserXpAvatar';
import { defaultAvatarUrl } from './constants';
import {
  getDisplayActivityScore,
  getUserActivityScore,
} from './components/levelUtils';

const OnlineUsersSidebar = ({ isOpen, closeSidebar }) => {
  const { onlineUsers } = useContext(OnlineUsersContext);
  const { userData, userId } = useContext(UserContext);
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [hoveredUserId, setHoveredUserId] = useState(null);
  // État pour gérer le hover sur le bouton de fermeture
  const [closeHovered, setCloseHovered] = useState(false);
  // États pour les appels vidéo
  const [showVideoCall, setShowVideoCall] = useState(false);
  const [videoCallTarget, setVideoCallTarget] = useState(null);
  const { t } = useLanguage();
  const [isMobileSmall, setIsMobileSmall] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 481 : false
  );

  const getOnlineUserDisplayScore = (user) => {
    const onlineUserId = user?.uid || user?.userId;

    return getDisplayActivityScore(
      getUserActivityScore(user, user?.messageCount ?? 0),
      onlineUserId
    );
  };
  
  // Ref pour stocker les fonctions de nettoyage
  const cleanupRef = useRef(null);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 500);
      setIsMobileSmall(window.innerWidth < 481);
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
      
      // Nettoyage de la présence et des ressources
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, []);

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  // Fonction pour lancer un appel vidéo
  const handleVideoCall = async (targetUser = null) => {
    try {
      console.log('[OnlineUsers] handleVideoCall appelé avec:', targetUser);
      
      const userToCall = targetUser || selectedUser;
      if (!userToCall) {
        console.error('[VideoCall] Aucun utilisateur sélectionné pour l\'appel');
        alert('Erreur: Aucun utilisateur sélectionné pour l\'appel');
        return;
      }
      
      console.log('[VideoCall] Lancement appel vidéo vers:', userToCall.username);
      console.log('[VideoCall] Données utilisateur:', userToCall);
      
      // 🔧 NOUVEAU : Fermer le modal AVANT de lancer l'appel
      handleCloseModal();
      
      // Attendre un peu pour que le modal se ferme
      setTimeout(() => {
        setVideoCallTarget(userToCall);
        setShowVideoCall(true);
      }, 300);
      
      console.log('[VideoCall] États mis à jour - showVideoCall:', true, 'target:', userToCall.username);
    } catch (error) {
      console.error('[VideoCall] Erreur dans handleVideoCall:', error);
      alert(`Erreur lors du lancement de l'appel: ${error.message}`);
    }
  };

  // Fonction pour fermer l'appel vidéo
  const handleCloseVideoCall = () => {
    console.log('[VideoCall] Fermeture appel vidéo');
    setShowVideoCall(false);
    setVideoCallTarget(null);
  };

  // Fonction pour fermer la modal avec nettoyage
  const handleCloseModal = () => {
    setShowModal(false);
    // Remettre à zéro l'utilisateur sélectionné pour libérer la mémoire
    setTimeout(() => {
      setSelectedUser(null);
    }, 300);
  };

  // Force le dimensionnement du modal côté JS pour les petits écrans
  // Pas de forçage JS sur les dimensions : on laisse le CSS gérer desktop et mobile
  useEffect(() => {
    if (!showModal) return;
    return () => {};
  }, [showModal, isMobileSmall]);

  // Style pour le bouton "fermer"
  const closeButtonStyle = {
    // Dimensions fixes pour un bouton rond
    width: isMobile ? '40px' : '50px',
    height: isMobile ? '40px' : '50px',

    background: '#000',
    border: 'none',
    color: '#FF33CC',
    fontSize: isMobile ? '1.2rem' : '1.5rem',
    cursor: 'pointer',
    outline: 'none',
    borderRadius: '50%', // bouton rond
    transition: 'all 0.3s ease',

    // Évite l'effet trop prononcé avec un scale moins important
    transform: closeHovered ? 'scale(1.08)' : 'scale(1)',

    // Ombres plus douces et moins « ovalique »
    boxShadow: closeHovered
      ? '0px 0px 10px rgba(0, 255, 255, 0.8), 0 0 15px rgba(255, 51, 204, 0.5)'
      : '0px 0px 6px rgba(0, 0, 0, 0.3)'
  };

  // Styles pour les boutons d'appel
  const callButtonStyle = {
    width: '50px',
    height: '50px',
    background: '#D90093',
    color: '#FFF',
    border: 'none',
    borderRadius: '50%',
    margin: '0 10px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 0 8px rgba(217, 0, 147, 0.5)',
  };

  const videoButtonStyle = {
    ...callButtonStyle,
    background: '#FF33CC',
  };

  return (
    <>
      <div className={`online-users-sidebar ${isOpen ? 'open' : ''}`} style={{ zIndex: '100003' }}>
        <button onClick={closeSidebar}>{t('common.close')}</button>
        {onlineUsers && onlineUsers.length > 0 ? (
          <ul style={{ margin: "0.5rem" }}>
            {onlineUsers
              .filter(user => user.uid !== userId)
              .map((user) => (
              <li
                key={user.uid}
                onClick={() => handleUserClick(user)}
                onMouseEnter={() => setHoveredUserId(user.uid)}
                onMouseLeave={() => setHoveredUserId(null)}
                style={{
                  filter: hoveredUserId === user.uid
                    ? 'drop-shadow(0px 0px 7.5px #00FFFF) brightness(1.2)'
                    : 'none',
                  cursor: 'pointer',
                }}
              >
                <div className="online-user-xp-avatar">
                  <UserXpAvatar
                    avatarUrl={user.avatar || defaultAvatarUrl}
                    activityScore={getOnlineUserDisplayScore(user)}
                    fallbackActivityScore={user?.messageCount ?? 0}
                    showTitle={false}
                    showActivity={false}
                    className="user-xp-avatar--online-sidebar"
                    userId={user.uid || user.userId}
                  />
                </div>

                <span className="online-user-name">{user.username}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p style={{
            margin: "1rem",
            fontSize: "0.75rem",
            color: "#00ffff",
            textAlign: "center",
            backgroundColor: "#200211",
            padding: "0.5rem",
            maxWidth: "clamp(300px, 50%, 500px)"
          }}>
            {t('onlineUsers.noUsersOnline')}
          </p>
        )}
        <Modal
          show={showModal}
          onHide={handleCloseModal}
          className="modalProfil"
          dialogClassName="emuleModal-dialog"
          contentClassName="emuleModal-content"
          backdropClassName="emuleModal-backdrop"
          centered
        >
          <Modal.Header className="modalProfil-header">
              {/* Rangée 1 : Boutons d'action */}
              <div className="modalProfil-actions">
                {/* Bouton d'appel audio - temporairement désactivé */}
                <button
                  onClick={handleVideoCall}
                  style={callButtonStyle}
                  title="Appel audio (prochainement)"
                >
                  <FontAwesomeIcon icon={faPhone} />
                </button>
                
                {/* Bouton de fermeture */}
                <button
                  onClick={handleCloseModal}
                  onMouseEnter={() => setCloseHovered(true)}
                  onMouseLeave={() => setCloseHovered(false)}
                  style={closeButtonStyle}
                >
                  <FontAwesomeIcon icon={faTimesCircle} style={{ fontSize: '1.5em' }} />
                </button>
                
                {/* Bouton d'appel vidéo */}
                <button
                  onClick={() => handleVideoCall(selectedUser)}
                  style={videoButtonStyle}
                  title="Lancer un appel vidéo"
                >
                  <FontAwesomeIcon icon={faVideo} />
                </button>
              </div>
              
              {/* Rangée 2 : Username et occupation */}
              <div className="modalProfil-meta">
                <Modal.Title className="modalProfil-left">
                  {selectedUser ? selectedUser.username : ''}
                </Modal.Title>
                <Modal.Title className="modalProfil-right">
                  {selectedUser ? selectedUser.occupation : ''}
                </Modal.Title>
              </div>
          </Modal.Header>
          <Modal.Body className="modalProfil-body"
          //  style={{ margin:'0 auto'}}
          >
            {selectedUser && <EmuleProfil selectedUser={selectedUser} />}
          </Modal.Body>
        </Modal>
      </div>
      
      {/* Interface d'appel vidéo */}
      {showVideoCall && videoCallTarget && (
        <VideoCallManager
          isOpen={showVideoCall}
          onClose={handleCloseVideoCall}
          targetUser={videoCallTarget}
          mode="outgoing"
        />
      )}
    </>
  );
};

export default OnlineUsersSidebar;
